/**
 * Cloud Storage & Authentication Client
 * 
 * 100% drop-in replacement for @supabase/supabase-js communicating with same-origin
 * /api/v1/* and /api/auth/* endpoints.
 */

const TOKEN_KEY = 'pulsefit_jwt';
const REFRESH_KEY = 'pulsefit_refresh_token';
const SESSION_KEY = 'pulsefit_session';
const USER_ID_KEY = 'pulsefit_user_id';

function getStorageItem(key) {
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(key);
    }
  } catch (_) {}
  return null;
}

function setStorageItem(key, value) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value);
    }
  } catch (_) {}
}

function removeStorageItem(key) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  } catch (_) {}
}

function safeAtob(str) {
  try {
    if (typeof atob === 'function') {
      return atob(str);
    }
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(str, 'base64').toString('utf8');
    }
  } catch (_) {}
  return '{}';
}

export function extractJwtExp(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) {
      b64 += '=';
    }
    const payload = JSON.parse(safeAtob(b64));
    if (typeof payload.exp === 'number') {
      return payload.exp;
    }
  } catch (_) {}
  return null;
}

export function syncNativeAuthTokens(newToken, newRefreshToken) {
  if (!newToken) return;
  setStorageItem(TOKEN_KEY, newToken);
  if (newRefreshToken) {
    setStorageItem(REFRESH_KEY, newRefreshToken);
  }
  const now = Math.floor(Date.now() / 1000);
  let exp = extractJwtExp(newToken) || (now + 3600);

  let user = null;
  try {
    const parts = newToken.split('.');
    if (parts.length === 3) {
      let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4 !== 0) b64 += '=';
      const payload = JSON.parse(safeAtob(b64));
      if (typeof payload.exp === 'number') exp = payload.exp;
      user = {
        id: payload.sub,
        email: payload.email,
        user_metadata: payload.user_metadata || {}
      };
    }
  } catch (_) {}

  let existingSession = {};
  const raw = getStorageItem(SESSION_KEY);
  if (raw) {
    try {
      existingSession = JSON.parse(raw);
    } catch (_) {
      existingSession = {};
    }
  }

  const resolvedRefreshToken = newRefreshToken || existingSession.refresh_token || getStorageItem(REFRESH_KEY) || '';
  const resolvedUser = user || existingSession.user || {};

  const session = {
    access_token: newToken,
    refresh_token: resolvedRefreshToken,
    token_type: 'bearer',
    expires_at: exp,
    user: resolvedUser
  };

  setStorageItem(SESSION_KEY, JSON.stringify(session));
  if (resolvedUser && resolvedUser.id) {
    setStorageItem(USER_ID_KEY, resolvedUser.id);
  }

  if (cloudClient && cloudClient.auth && typeof cloudClient.auth._notify === 'function') {
    cloudClient.auth._notify('SIGNED_IN', session);
  }

  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    const EventConstructor = typeof CustomEvent !== 'undefined' ? CustomEvent : (typeof window.CustomEvent !== 'undefined' ? window.CustomEvent : null);
    if (EventConstructor) {
      window.dispatchEvent(new EventConstructor('pulsefit:auth-synced', {
        detail: { session, token: newToken, refreshToken: resolvedRefreshToken }
      }));
    }
  }
}

if (typeof window !== 'undefined') {
  window.syncNativeAuthTokens = syncNativeAuthTokens;
}
if (typeof globalThis !== 'undefined') {
  globalThis.syncNativeAuthTokens = syncNativeAuthTokens;
}

export class CloudQueryBuilder {
  constructor(apiBaseOrTable, tableOrApiBase, getAuthToken, refreshAuthToken = null) {
    let apiBase = apiBaseOrTable;
    let table = tableOrApiBase;
    if (typeof apiBaseOrTable === 'string' && !apiBaseOrTable.startsWith('/') && !apiBaseOrTable.startsWith('http') && typeof tableOrApiBase === 'string' && (tableOrApiBase.startsWith('/') || tableOrApiBase.startsWith('http'))) {
      table = apiBaseOrTable;
      apiBase = tableOrApiBase;
    }
    this.apiBase = apiBase || '/api/v1';
    this.table = table;
    this.getAuthToken = getAuthToken;
    this.refreshAuthToken = refreshAuthToken;
    this.operation = 'SELECT';
    this.selectColumns = '*';
    this.filters = [];
    this.orders = [];
    this.limitVal = null;
    this.isSingle = false;
    this.isMaybeSingle = false;
    this.payload = null;
    this.upsertOptions = {};
  }

  select(columns = '*') {
    this.selectColumns = columns;
    return this;
  }

  eq(column, value) {
    this.filters.push({ column, op: 'eq', value });
    return this;
  }

  neq(column, value) {
    this.filters.push({ column, op: 'neq', value });
    return this;
  }

  ilike(column, value) {
    this.filters.push({ column, op: 'ilike', value });
    return this;
  }

  in(column, values) {
    this.filters.push({ column, op: 'in', value: values });
    return this;
  }

  order(column, { ascending = true } = {}) {
    this.orders.push({ column, ascending });
    return this;
  }

  limit(count) {
    this.limitVal = count;
    return this;
  }

  single() {
    this.isSingle = true;
    return this;
  }

  maybeSingle() {
    this.isMaybeSingle = true;
    return this;
  }

  insert(payload) {
    this.operation = 'INSERT';
    this.payload = payload;
    return this;
  }

  upsert(payload, options = {}) {
    this.operation = 'UPSERT';
    this.payload = payload;
    this.upsertOptions = options;
    return this;
  }

  update(payload) {
    this.operation = 'UPDATE';
    this.payload = payload;
    return this;
  }

  delete() {
    this.operation = 'DELETE';
    return this;
  }

  then(resolve, reject) {
    return this._execute().then(resolve, reject);
  }

  catch(reject) {
    return this._execute().catch(reject);
  }

  async _execute() {
    const idFilter = this.filters.find(f => f.column === 'id' && f.op === 'eq');
    let method = 'GET';
    let urlPath = `${this.apiBase}/${this.table}`;

    if (this.operation === 'INSERT') {
      method = 'POST';
    } else if (this.operation === 'UPSERT') {
      method = 'POST';
    } else if (this.operation === 'UPDATE') {
      method = 'PATCH';
      if (this.table === 'profiles') {
        urlPath = `${this.apiBase}/profiles/me`;
        method = 'PUT';
      } else if (idFilter && (this.table === 'workouts' || this.table === 'planned_workouts')) {
        urlPath = `${this.apiBase}/${this.table}/${encodeURIComponent(idFilter.value)}`;
      }
    } else if (this.operation === 'DELETE') {
      method = 'DELETE';
      if (idFilter && (this.table === 'workouts' || this.table === 'planned_workouts')) {
        urlPath = `${this.apiBase}/${this.table}/${encodeURIComponent(idFilter.value)}`;
      }
    } else if (this.operation === 'SELECT') {
      method = 'GET';
      if (this.table === 'profiles') {
        urlPath = `${this.apiBase}/profiles/me`;
      } else if (idFilter && (this.isSingle || this.isMaybeSingle) && this.table === 'workouts') {
        urlPath = `${this.apiBase}/${this.table}/${encodeURIComponent(idFilter.value)}`;
      }
    }

    const queryParams = new URLSearchParams();

    for (const f of this.filters) {
      if (this.table === 'profiles' && f.column === 'id') {
        continue;
      }
      if (f.column === 'id' && f.op === 'eq' && urlPath.endsWith(`/${encodeURIComponent(f.value)}`)) {
        continue;
      }
      if (f.op === 'eq') {
        queryParams.append(f.column, String(f.value));
      } else if (f.op === 'neq') {
        queryParams.append(`${f.column}_neq`, String(f.value));
      } else if (f.op === 'ilike') {
        queryParams.append(`${f.column}_ilike`, String(f.value));
      } else if (f.op === 'in') {
        queryParams.append(f.column, Array.isArray(f.value) ? f.value.join(',') : String(f.value));
      }
    }

    if (this.orders.length > 0) {
      const o = this.orders[0];
      queryParams.append('order_by', o.column);
      queryParams.append('order_direction', o.ascending ? 'asc' : 'desc');
    }

    if (this.limitVal != null) {
      queryParams.append('limit', String(this.limitVal));
    }

    if (this.operation === 'UPSERT') {
      const conflictField = this.upsertOptions.onConflict || (this.upsertOptions.options && this.upsertOptions.options.onConflict);
      if (conflictField) {
        queryParams.append('on_conflict', conflictField);
      }
    }

    const queryString = queryParams.toString();
    const finalUrl = queryString ? `${urlPath}?${queryString}` : urlPath;

    const headers = { 'Content-Type': 'application/json' };
    const token = typeof this.getAuthToken === 'function' ? this.getAuthToken() : null;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (this.operation === 'UPSERT') {
      headers['Prefer'] = 'resolution=merge-duplicates';
    }

    const reqOptions = {
      method,
      headers
    };

    if (this.payload !== null && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      reqOptions.body = JSON.stringify(this.payload);
    }

    let res;
    try {
      res = await fetch(finalUrl, reqOptions);
    } catch (netErr) {
      return { data: null, error: netErr };
    }

    if (res.status === 401 && typeof this.refreshAuthToken === 'function') {
      try {
        const refreshedSession = await this.refreshAuthToken();
        const newAccessToken = refreshedSession?.access_token || refreshedSession?.data?.session?.access_token;
        if (newAccessToken) {
          const retryHeaders = { ...headers, Authorization: `Bearer ${newAccessToken}` };
          const retryOptions = { ...reqOptions, headers: retryHeaders };
          res = await fetch(finalUrl, retryOptions);
        }
      } catch (_) {}
    }

    if (res.status === 204) {
      return { data: null, error: null };
    }

    if (!res.ok) {
      if (res.status === 404 && this.isMaybeSingle) {
        return { data: null, error: null };
      }
      if (res.status === 404 && this.isSingle) {
        return { data: null, error: { message: 'Row not found', code: 'PGRST116', status: 404 } };
      }
      const errBody = await res.json().catch(() => ({}));
      const error = {
        message: errBody.detail || errBody.message || res.statusText || `Request failed with status ${res.status}`,
        status: res.status
      };
      return { data: null, error };
    }

    let data = null;
    try {
      data = await res.json();
    } catch (_) {
      data = null;
    }

    if (Array.isArray(data)) {
      if (this.isSingle) {
        if (data.length === 0) {
          return { data: null, error: { message: 'Row not found', code: 'PGRST116', status: 404 } };
        }
        return { data: data[0], error: null };
      }
      if (this.isMaybeSingle) {
        return { data: data.length > 0 ? data[0] : null, error: null };
      }
      return { data, error: null };
    }

    return { data, error: null };
  }
}

export class CloudAuthClient {
  constructor(authBase = '/api/auth') {
    this.authBase = authBase;
    this.listeners = [];
  }

  getAccessToken() {
    const sessionRaw = getStorageItem('pulsefit_session');
    if (sessionRaw) {
      try {
        const session = JSON.parse(sessionRaw);
        if (session && session.access_token) return session.access_token;
      } catch (_) {}
    }
    return getStorageItem('pulsefit_jwt');
  }

  _notify(event, session) {
    for (const listener of this.listeners) {
      try {
        listener(event, session);
      } catch (e) {
        console.warn("Auth listener error:", e);
      }
    }
  }

  _handleAuthSuccess(data, event = 'SIGNED_IN') {
    if (!data || !data.access_token) return null;
    setStorageItem('pulsefit_jwt', data.access_token);
    if (data.refresh_token) {
      setStorageItem('pulsefit_refresh_token', data.refresh_token);
    }
    const session = {
      access_token: data.access_token,
      token_type: data.token_type || 'bearer',
      expires_in: data.expires_in || 3600,
      expires_at: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
      refresh_token: data.refresh_token || getStorageItem('pulsefit_refresh_token') || '',
      user: data.user || {}
    };
    setStorageItem('pulsefit_session', JSON.stringify(session));
    if (session.user && session.user.id) {
      setStorageItem(USER_ID_KEY, session.user.id);
    }
    this._notify(event, session);
    return session;
  }

  async _refreshSession(refreshToken) {
    const isNativeCompanion = Boolean(
      typeof window !== 'undefined' &&
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.authHandler
    );

    if (isNativeCompanion) {
      const nativeSession = await new Promise((resolve) => {
        let timer = null;
        const cleanup = () => {
          if (timer) clearTimeout(timer);
          if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
            window.removeEventListener('pulsefit:auth-synced', onAuthSynced);
          }
        };

        const onAuthSynced = (event) => {
          cleanup();
          const session = event?.detail?.session;
          if (session && session.access_token) {
            resolve(session);
          } else {
            resolve(null);
          }
        };

        if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
          window.addEventListener('pulsefit:auth-synced', onAuthSynced);
        }

        timer = setTimeout(() => {
          cleanup();
          resolve(null);
        }, 2500);

        try {
          window.webkit.messageHandlers.authHandler.postMessage({ action: 'refreshToken' });
        } catch (postErr) {
          cleanup();
          resolve(null);
        }
      });

      if (nativeSession) {
        return nativeSession;
      }
      if (!refreshToken) {
        return null;
      }
    }

    try {
      const res = await fetch(`${this.authBase}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          refresh_token: refreshToken
        })
      });
      if (!res.ok) {
        const legacyRes = await fetch(`/auth/v1/token?grant_type=refresh_token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken })
        });
        if (!legacyRes.ok) return null;
        const data = await legacyRes.json();
        return this._handleAuthSuccess(data, 'TOKEN_REFRESHED');
      }
      const data = await res.json();
      return this._handleAuthSuccess(data, 'TOKEN_REFRESHED');
    } catch (_) {
      return null;
    }
  }

  async refreshSession() {
    let refreshToken = getStorageItem(REFRESH_KEY);
    if (!refreshToken) {
      const raw = getStorageItem(SESSION_KEY);
      if (raw) {
        try {
          const s = JSON.parse(raw);
          refreshToken = s.refresh_token;
        } catch (_) {}
      }
    }
    const isNativeCompanion = Boolean(
      typeof window !== 'undefined' &&
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.authHandler
    );
    if (!refreshToken && !isNativeCompanion) return null;
    const res = await this._refreshSession(refreshToken || '');
    return res?.data?.session || res || null;
  }

  async signInWithPassword({ email, password }) {
    try {
      const res = await fetch(`${this.authBase}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const error = new Error(body.detail || body.message || 'Login failed');
        return { data: { user: null, session: null }, error };
      }
      const session = this._handleAuthSuccess(body, 'SIGNED_IN');
      return { data: { user: session ? session.user : body.user, session }, error: null };
    } catch (err) {
      return { data: { user: null, session: null }, error: err };
    }
  }

  async signUp({ email, password, options = {} }) {
    try {
      const res = await fetch(`${this.authBase}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, options })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const error = new Error(body.detail || body.message || 'Signup failed');
        return { data: { user: null, session: null }, error };
      }
      const session = this._handleAuthSuccess(body, 'SIGNED_IN');
      return { data: { user: session ? session.user : body.user, session }, error: null };
    } catch (err) {
      return { data: { user: null, session: null }, error: err };
    }
  }

  async signOut() {
    try {
      const token = this.getAccessToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${this.authBase}/logout`, {
        method: 'POST',
        headers
      }).catch(() => {});
    } finally {
      removeStorageItem('pulsefit_jwt');
      removeStorageItem('pulsefit_refresh_token');
      removeStorageItem('pulsefit_session');
      removeStorageItem(USER_ID_KEY);
      this._notify('SIGNED_OUT', null);
    }
    return { error: null };
  }

  async getSession() {
    const raw = getStorageItem(SESSION_KEY);
    const now = Math.floor(Date.now() / 1000);

    if (!raw) {
      const jwt = getStorageItem(TOKEN_KEY);
      if (!jwt) {
        return { data: { session: null }, error: null };
      }
      try {
        const exp = extractJwtExp(jwt) || (now + 3600);
        const parts = jwt.split('.');
        if (parts.length === 3) {
          let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
          while (b64.length % 4 !== 0) b64 += '=';
          const payload = JSON.parse(safeAtob(b64));
          const user = { id: payload.sub, email: payload.email, user_metadata: payload.user_metadata || {} };
          const session = {
            access_token: jwt,
            token_type: 'bearer',
            expires_at: exp,
            refresh_token: getStorageItem(REFRESH_KEY) || '',
            user
          };
          if (exp <= now + 30) {
            const refreshToken = session.refresh_token || getStorageItem(REFRESH_KEY);
            const isNativeCompanion = Boolean(
              typeof window !== 'undefined' &&
              window.webkit &&
              window.webkit.messageHandlers &&
              window.webkit.messageHandlers.authHandler
            );
            if ((refreshToken || isNativeCompanion) && (typeof fetch !== 'undefined' || isNativeCompanion)) {
              const refreshed = await this._refreshSession(refreshToken || '');
              const refreshedSession = refreshed?.data?.session || refreshed;
              if (refreshedSession && refreshedSession.access_token) {
                return { data: { session: refreshedSession }, error: null };
              }
              const latestJwt = getStorageItem(TOKEN_KEY);
              if (latestJwt && latestJwt !== jwt) {
                const latestExp = extractJwtExp(latestJwt) || (now + 3600);
                if (latestExp > now) {
                  session.access_token = latestJwt;
                  session.refresh_token = getStorageItem(REFRESH_KEY) || session.refresh_token;
                  session.expires_at = latestExp;
                  setStorageItem(SESSION_KEY, JSON.stringify(session));
                  return { data: { session }, error: null };
                }
              }
              if (exp <= now) {
                removeStorageItem(TOKEN_KEY);
                removeStorageItem(REFRESH_KEY);
                removeStorageItem(SESSION_KEY);
                return { data: { session: null }, error: null };
              }
            }
          }
          setStorageItem(SESSION_KEY, JSON.stringify(session));
          return { data: { session }, error: null };
        }
      } catch (_) {}
      return { data: { session: null }, error: null };
    }

    try {
      const session = JSON.parse(raw);
      const token = session.access_token || getStorageItem(TOKEN_KEY);
      const exp = session.expires_at || extractJwtExp(token);

      if (exp && exp <= now + 30) {
        const refreshToken = getStorageItem(REFRESH_KEY) || session.refresh_token;
        const isNativeCompanion = Boolean(
          typeof window !== 'undefined' &&
          window.webkit &&
          window.webkit.messageHandlers &&
          window.webkit.messageHandlers.authHandler
        );
        let refreshAttempted = false;
        if ((refreshToken || isNativeCompanion) && (typeof fetch !== 'undefined' || isNativeCompanion)) {
          refreshAttempted = true;
          const refreshed = await this._refreshSession(refreshToken || '');
          const refreshedSession = refreshed?.data?.session || refreshed;
          if (refreshedSession && refreshedSession.access_token) {
            return { data: { session: refreshedSession }, error: null };
          }
        }

        const latestToken = getStorageItem(TOKEN_KEY);
        if (latestToken && latestToken !== token) {
          session.access_token = latestToken;
          session.refresh_token = getStorageItem(REFRESH_KEY) || session.refresh_token;
          session.expires_at = extractJwtExp(latestToken) || (now + 3600);
          setStorageItem(SESSION_KEY, JSON.stringify(session));
          return { data: { session }, error: null };
        }

        if (refreshAttempted && exp <= now) {
          removeStorageItem(TOKEN_KEY);
          removeStorageItem(REFRESH_KEY);
          removeStorageItem(SESSION_KEY);
          return { data: { session: null }, error: null };
        }
      }

      return { data: { session }, error: null };
    } catch (_) {
      return { data: { session: null }, error: null };
    }
  }

  async resetPasswordForEmail(email, options = {}) {
    try {
      const res = await fetch(`${this.authBase}/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...options })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const error = new Error(body.detail || body.message || 'Password recovery failed');
        return { data: null, error };
      }
      return { data: body, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  onAuthStateChange(callback) {
    if (typeof callback === 'function') {
      this.listeners.push(callback);
    }
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            this.listeners = this.listeners.filter(cb => cb !== callback);
          }
        }
      }
    };
  }
}

export class CloudClient {
  constructor(options = {}) {
    this.apiBase = options.apiBase || '/api/v1';
    this.authBase = options.authBase || '/api/auth';
    this.authModule = new CloudAuthClient(this.authBase);
  }

  get auth() {
    return this.authModule;
  }

  getAuthToken() {
    return this.authModule.getAccessToken();
  }

  from(table) {
    return new CloudQueryBuilder(
      this.apiBase,
      table,
      () => this.getAuthToken(),
      () => this.auth.refreshSession()
    );
  }

  rpc(name, params = {}) {
    return this.callRpc(name, params);
  }

  async callRpc(name, params = {}) {
    try {
      const token = this.getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${this.apiBase}/rpc/${name}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(params)
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        return { data, error: null };
      }

      if (name === 'delete_own_account' && res.status === 404) {
        const fallbackRes = await fetch(`${this.apiBase}/account`, {
          method: 'DELETE',
          headers
        });
        if (fallbackRes.ok) {
          const data = await fallbackRes.json().catch(() => ({}));
          return { data, error: null };
        }
      }

      const err = await res.json().catch(() => ({ detail: res.statusText }));
      return {
        data: null,
        error: { message: err.detail || err.message || `RPC ${name} failed`, status: res.status }
      };
    } catch (err) {
      return { data: null, error: err };
    }
  }
}

export function initCloudClient(options = {}) {
  return new CloudClient(options);
}

export const cloudClient = initCloudClient();
